import React, { useState, useContext } from 'react';
import { useParams } from "react-router-dom";
import 'bulma/css/bulma.min.css';

import { Uid } from "../../utils.tsx";
import { ScanTypeSlug, ScanType, Parameter } from '../../scanTypes.ts';
import { BeamlineContext } from '../../beamlineProvider.tsx';
import { ParameterSettingsCreateDto } from '../../api/api.ts';
import { SelectingStatus } from '../../components/inputAutocomplete.tsx'
import { ParameterAutocomplete, ParameterSearchFunctions } from '../../components/parameterAutocomplete.tsx';


interface AddParameterSettingsProps {
  scantypeslug: ScanTypeSlug;
}


const AddParameterSettings: React.FC<AddParameterSettingsProps> = (props) => {

  const beamlineContext = useContext(BeamlineContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [overriddenDefault, setOverriddenDefault] = useState<string>("");

  const [parameterValue, setParameterValue] = useState<Parameter | null>(null)
  const [validAllInput, setValidAllInput] = useState<boolean>(true);

  const [inProgress, setInProgress] = useState<boolean>(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);


  function getScanType(): ScanType | undefined {
    if (!props.scantypeslug) { return undefined; }
    return beamlineContext.scanTypes.typesBySlug.get(props.scantypeslug);
  }


  function getParameterIdsNotInScanType(): Uid[] {
    const thisScanType = getScanType();
    if (!thisScanType) { return []; }
    const parameterIdsInScanType = new Set(thisScanType.parameterIds);
    const parametersNotInScanType = beamlineContext.scanTypes.parameterIdsInDisplayOrder.filter((p) => !parameterIdsInScanType.has(p));
    return parametersNotInScanType;
  }


  function clickedOpen() {
    const thisScanType = getScanType();
    if (!inProgress && !isOpen && thisScanType) {
      setIsOpen(true);
      const parametersNotInScanType = getParameterIdsNotInScanType();
      const firstValidId = parametersNotInScanType[0];
      const initialParameterValue = firstValidId ? beamlineContext.scanTypes.parametersById.get(firstValidId)! : null;
      setParameterValue(initialParameterValue);
      validate(initialParameterValue);
    }
  }

  function changedDefault(c:React.ChangeEvent<HTMLInputElement>) {
    const v = c.target.value;
    setOverriddenDefault(v);
  }

  function validate(parameter: Parameter | null) {
    var validParameter:boolean = false;

    const thisScanType = getScanType();
    if (parameter && thisScanType) {
      const parameterIdsInScanType = new Set(thisScanType.parameterIds);
      validParameter = !parameterIdsInScanType.has(parameter.id);
    }
    setValidAllInput(validParameter);
  }


  async function pressedSubmit() {
    const thisScanType = getScanType();
    if (!thisScanType) { return; }

    setSubmitErrorMessage(null);
    setInProgress(true);

    const dto: ParameterSettingsCreateDto = {
      slug_scan_type: props.scantypeslug,
      id_parameter: parameterValue!.id,
    }
    if (overriddenDefault.trim()) {
      dto.default = overriddenDefault.trim();
    }

    const result = await beamlineContext.scanTypes.addParameterToScanType(dto);
    if (result.success) {
      beamlineContext.changed();
      setIsOpen(false);
    } else {
      setSubmitErrorMessage(result.message || "Error adding parameter to scan type.");
    }
    setInProgress(false);
  }


  function clickedClose() {
    if (!inProgress && isOpen) { setSubmitErrorMessage(null); setIsOpen(false); }
  }


  const parameterSearchFunctions:ParameterSearchFunctions = {
    itemSelected: (item: Parameter) => { setParameterValue(item); validate(item); return SelectingStatus.Success },
    selectedNone: () => { setParameterValue(null); validate(null); return SelectingStatus.Success }
  };


  return (
    <div>

      <button className="button" onClick={ clickedOpen }>Add Parameter To Scan Type</button>

      <div id="modal-add-sample" className={ isOpen ? "modal is-active" : "modal" }>
        <div className="modal-background"></div>

        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Add Parameter To Scan Type</p>
            <button className="delete" aria-label="close" onClick={ clickedClose }></button>

          </header>
          <section className="modal-card-body">

            <div className="field">
              <label className="label">Parameter</label>
                <ParameterAutocomplete
                    selectedItem={parameterValue}
                    searchFunctions={parameterSearchFunctions}
                    allowedParameters={getParameterIdsNotInScanType()}
                  />
            </div>

            <div className="field">
              <label className="label">Default</label>
              <div className="control">
                <input className="input"
                  type="text"
                  placeholder="Optional default value specific to this Scan Type"
                  value={ overriddenDefault }
                  onChange={ changedDefault } />
              </div>
            </div>

            <div className="buttons">
              <input
                type="button"
                className="button is-success"
                onClick={ pressedSubmit }
                disabled={ inProgress || !validAllInput }
                value="Add"
              />
              <button className="button" onClick={ clickedClose }>Cancel</button>
            </div>

            { submitErrorMessage && (
              <article className="message is-danger">
                <div className="message-body">
                  { submitErrorMessage }
                </div>
              </article>
            )}

          </section>

        </div>
      </div>

    </div>
  )
}
export default AddParameterSettings
